import { useMemo, useState } from "react";
import Reveal from "@/components/Reveal";
import { channels } from "@/mocks/site";
import type { ChannelKey, NewsItem } from "@/mocks/types";

interface LatestFeedProps {
  news: NewsItem[];
  onRead: (item: NewsItem) => void;
}

const feelMap: Record<NewsItem["feel"], string> = {
  "实锤度高": "实锤度高",
  "多方互证": "多方互证",
  "网传中": "网传中",
  "纯属推测": "纯属推测",
};

export default function LatestFeed({ news, onRead }: LatestFeedProps) {
  const [active, setActive] = useState<ChannelKey | "all">("all");

  const tabs = useMemo(
    () => [{ key: "all" as const, name: "全部" }, ...channels],
    []
  );

  const filtered = useMemo(() => {
    if (active === "all") return news;
    return news.filter((n) => n.channel === active);
  }, [active, news]);

  const countOf = (key: ChannelKey | "all") =>
    key === "all" ? news.length : news.filter((n) => n.channel === key).length;

  return (
    <section id="latest" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-primary-600">
                <span className="h-px w-8 bg-primary-400"></span> Latest · 今日爆瓜
              </p>
              <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-900 md:text-[34px]">
                今日在传的瓜讯
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                未官宣行程、资源瓜、新戏饼、综艺邀约、商务代言与空降活动的最新线索，按栏目切换查看。
              </p>
            </div>
            <p className="flex items-center gap-2 whitespace-nowrap text-sm text-foreground-500">
              <i className="ri-refresh-line text-primary-500" aria-hidden="true"></i>
              最近更新 · 9 月 6 日
            </p>
          </div>
        </Reveal>

        {/* tabs */}
        <Reveal delay={60}>
          <div className="no-scrollbar mt-7 flex items-center gap-2 overflow-x-auto pb-1">
            {tabs.map((t) => {
              const isAll = t.key === "all";
              const name = isAll ? "全部" : t.name;
              const activeCls =
                active === t.key
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-background-200 border border-background-200";
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all ${activeCls}`}
                >
                  {name}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                      active === t.key ? "bg-accent-500 text-foreground-950" : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {countOf(t.key)}
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* grid */}
        <div className="mt-7 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item, idx) => {
            const ch = channels.find((c) => c.key === item.channel);
            return (
              <Reveal key={item.id} delay={(idx % 3) * 60}>
                <article
                  className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
                  onClick={() => onRead(item)}
                >
                  <span className="pointer-events-none absolute -right-1 top-0 select-none font-heading text-[88px] font-black leading-none text-background-100 transition-colors group-hover:text-primary-100/70">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="relative flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-100 px-2 py-1 text-xs font-semibold text-primary-800">
                        <i className={ch?.icon ?? "ri-hashtag"} aria-hidden="true"></i>
                        {ch?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{item.date}</span>
                    </div>
                    <span
                      className={`whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-bold ${
                        item.feel === "实锤度高"
                          ? "bg-accent-100 text-accent-800"
                          : item.feel === "多方互证"
                          ? "bg-secondary-100 text-secondary-800"
                          : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {feelMap[item.feel]}
                    </span>
                  </div>

                  <h3 className="relative mt-3 font-heading text-[17px] font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 line-clamp-2 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="relative mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                    {item.excerpt}
                  </p>

                  <div className="relative mt-4 flex flex-1 items-end justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <i className="ri-record-circle-line text-primary-500" aria-hidden="true"></i>
                      <span className="max-w-[150px] truncate">{item.source}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-accent-700">
                      <i className="ri-fire-fill" aria-hidden="true"></i>
                      {(item.heat / 1000).toFixed(1)}k
                    </span>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}